import React, { useEffect, useState } from "react";
import useApp from "../../../utils/useApp";
import { Button, Col, Container, Dropdown, DropdownButton, Row, Spinner, Stack } from "react-bootstrap";
import { fetchAllResources, fetchResourceCategories, postResourceCategory } from "../../../services/httpClient";
import { Resource } from "../../../types/resource";
import { unsavedChanges } from "../../../App";
import SaveChangesModal from "../../ui/SaveChangesModal";
import { faAdd, faClose, faFilter } from "@fortawesome/free-solid-svg-icons";
import OrgResourceCard from "./OrgResourceCard";
import IconEmbeddedButton from "../../ui/IconEmbeddedButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "react-tooltip";
import AddResource from "./AddResource";

interface OrgResourcesState {
  setOrgToSave: any;
  getData: boolean;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const OrgResources = ({ setOrgToSave, getData, setShowToast, setToastText, setToastBody }: OrgResourcesState) => {
  const { auth, showMenu }: any = useApp();
  const [showAddResource, setShowAddResource] = useState<boolean>(false);
  const [showAddConsumable, setShowAddConsumable] = useState<boolean>(false);
  const [showAddNonConsumable, setShowAddNonConsumable] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [resources, setResources] = useState<Resource[]>([]);
  const [consumables, setConsumables] = useState<Resource[]>([]);
  const [filteredConsumables, setFilteredConsumables] = useState<Resource[]>([]);
  const [nonConsumables, setNonConsumables] = useState<Resource[]>([]);
  const [filteredNonConsumables, setFilteredNonConsumables] = useState<Resource[]>([]);
  const [resourceCategories, setResourceCategories] = useState<any[]>([]);
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [disableType, setDisableType] = useState<boolean>(false);
  const [resourceType, setResourceType] = useState<string>("0");
  const [filter, setFilter] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errMsg, setErrMsg] = useState<string>("");

  const showConsumableSection = typeFilter === "all" || typeFilter === "consumable";
  const showNonConsumableSection = typeFilter === "all" || typeFilter === "nonconsumable";

  useEffect(() => {
    if (getData) {
      getResources();
      getResourceCategories();
    }
  }, [getData]);

  useEffect(() => {
    getResources();
    getResourceCategories();
  }, [auth?.profile]);

  const getResources = () => {
    setIsLoading(true);
    setFilter(null);
    fetchAllResources()
      .then((res: any) => {
        const resources = res[0].data;
        setResources(resources);
        let filteredResources = [...resources];

        const consumables = filteredResources.filter((r: any) => r.type_key === "consumable");
        const nonConsumables = filteredResources.filter((r: any) => r.type_key === "nonconsumable");

        setConsumables(consumables);
        setFilteredConsumables(consumables);
        setNonConsumables(nonConsumables);
        setFilteredNonConsumables(nonConsumables);
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getResourceCategories = async () => {
    await fetchResourceCategories()
      .then((res: any) => {
        let cats = res[0].data;
        cats = [{ id: 0, key: "0", name: "None" }, ...cats];
        setResourceCategories(cats);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onClickAddResourceCategory = (newCategoryName: string) => {
    return postResourceCategory({ name: newCategoryName })
      .then((res: any) => {
        getResourceCategories();
        return { key: newCategoryName, name: newCategoryName, id: res[0].data.category_id };
      })
      .catch((err: any) => {
        console.log(err);
        throw err;
      });
  };

  const onClickAddResource = (type?: string) => {
    setErrMsg("");
    if (type) {
      if (type === "consumable") {
        setShowAddConsumable(true);
      } else {
        setShowAddNonConsumable(true);
      }
      setDisableType(true);
      setResourceType(type);
    } else {
      setShowAddResource(true);
      setDisableType(false);
      setResourceType("0");
    }
  };

  const onClickAddResourceCancel = () => {
    unsavedChanges.value = false;
    setErrMsg("");
    setShowAddResource(false);
    setShowAddConsumable(false);
    setShowAddNonConsumable(false);
    setOrgToSave(false);
    getResources();
  };

  const onClickFilterType = (type: string) => {
    setTypeFilter(type);
    setShowAddResource(false);
    setShowAddConsumable(false);
    setShowAddNonConsumable(false);
  };

  const onClickFilterResource = (selectedFilter: string | null) => {
    if (filter === selectedFilter) {
      setFilter(null);
      setFilteredConsumables(consumables);
      setFilteredNonConsumables(nonConsumables);
      return;
    }

    setFilter(selectedFilter);
    const filteredConsumables = consumables.filter((r) => r.category_key === (selectedFilter === "0" ? null : selectedFilter));
    const filteredNonConsumables = nonConsumables.filter((r) => r.category_key === (selectedFilter === "0" ? null : selectedFilter));
    setFilteredConsumables(filteredConsumables);
    setFilteredNonConsumables(filteredNonConsumables);
  };

  return (
    <>
      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <Row>
            <Col xs={3}>
              <h4 className="app-header-text">Resources</h4>
            </Col>
          </Row>
          {resources.length === 0 ? (
            <>
              <p className="mt-2">No Current Resources</p>
            </>
          ) : (
            <>
              <Row>
                <Col className="d-flex justify-content-center gap-3">
                  <Button
                    className={typeFilter === "all" ? "app-primary-color button" : "app-primary-color-inverse button"}
                    onClick={() => onClickFilterType("all")}
                  >
                    View All
                  </Button>
                  <Button
                    className={typeFilter === "consumable" ? "app-primary-color button" : "app-primary-color-inverse button"}
                    onClick={() => onClickFilterType("consumable")}
                  >
                    Consumable
                  </Button>
                  <Button
                    className={typeFilter === "nonconsumable" ? "app-primary-color button" : "app-primary-color-inverse button"}
                    onClick={() => onClickFilterType("nonconsumable")}
                  >
                    Non-Consumable
                  </Button>
                  <div data-tooltip-id="tooltip-filter" data-tooltip-content={"Category Filter"}>
                    <DropdownButton
                      id={filter ? "dropdown-filter-button-active" : "dropdown-filter-button"}
                      title={<FontAwesomeIcon icon={faFilter} />}
                    >
                      {resourceCategories?.map((cat: any) => (
                        <Dropdown.Item
                          key={cat.id}
                          href={`#/action-1`}
                          className={filter === cat.key ? "app-tertiary-color-inverse" : ""}
                          onClick={() => onClickFilterResource(cat.key)}
                        >
                          <text className="text-capitalize">{cat.name}</text>
                          {filter === cat.key ? <FontAwesomeIcon icon={faClose} className="ms-2" /> : ""}
                        </Dropdown.Item>
                      ))}
                    </DropdownButton>
                  </div>
                  <Tooltip id="tooltip-filter" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col xs={12}>
                  {showConsumableSection && (
                    <>
                      <Stack direction="horizontal" className="mt-4 mb-2 justify-content-between">
                        <h5 className="app-header-text m-0">Consumable</h5>
                        <IconEmbeddedButton
                          onClickButton={() => onClickAddResource("consumable")}
                          tooltipText="Add"
                          icon={faAdd}
                          disabled={showAddConsumable || showAddNonConsumable}
                        />
                      </Stack>
                      {showAddConsumable && (
                        <AddResource
                          setOrgToSave={setOrgToSave}
                          disableType={disableType}
                          type={resourceType}
                          category={filter}
                          setShowAddResource={setShowAddResource}
                          setShowAddConsumable={setShowAddConsumable}
                          setShowAddNonConsumable={setShowAddNonConsumable}
                          getResources={getResources}
                          resourceCategories={resourceCategories}
                          getResourceCategories={getResourceCategories}
                          onAddResourceCategory={onClickAddResourceCategory}
                          setErrMsg={setErrMsg}
                          setShowToast={setShowToast}
                          setToastText={setToastText}
                          setToastBody={setToastBody}
                        />
                      )}
                      {filteredConsumables.length === 0 && !showAddConsumable ? (
                        <p className="mt-2">No Resources</p>
                      ) : (
                        filteredConsumables.map((resource: Resource, index: number) => (
                          <OrgResourceCard
                            key={index}
                            resource={resource}
                            getResources={getResources}
                            resourceCategories={resourceCategories}
                            onAddResourceCategory={onClickAddResourceCategory}
                            setOrgToSave={setOrgToSave}
                            setShowToast={setShowToast}
                            setToastText={setToastText}
                            setToastBody={setToastBody}
                            setErrMsg={setErrMsg}
                          />
                        ))
                      )}
                    </>
                  )}
                  {showNonConsumableSection && (
                    <>
                      <Stack direction="horizontal" className="mt-4 mb-2 justify-content-between">
                        <h5 className="app-header-text m-0">Non-Consumable</h5>
                        <IconEmbeddedButton
                          onClickButton={() => onClickAddResource("nonconsumable")}
                          tooltipText="Add"
                          icon={faAdd}
                          disabled={showAddConsumable || showAddNonConsumable}
                        />
                      </Stack>
                      {showAddNonConsumable && (
                        <AddResource
                          setOrgToSave={setOrgToSave}
                          disableType={disableType}
                          type={resourceType}
                          category={filter}
                          setShowAddResource={setShowAddResource}
                          setShowAddConsumable={setShowAddConsumable}
                          setShowAddNonConsumable={setShowAddNonConsumable}
                          getResources={getResources}
                          resourceCategories={resourceCategories}
                          getResourceCategories={getResourceCategories}
                          onAddResourceCategory={onClickAddResourceCategory}
                          setErrMsg={setErrMsg}
                          setShowToast={setShowToast}
                          setToastText={setToastText}
                          setToastBody={setToastBody}
                        />
                      )}
                      {filteredNonConsumables.length === 0 && !showAddNonConsumable ? (
                        <p className="mt-2">No Resources</p>
                      ) : (
                        filteredNonConsumables.map((resource: Resource, index: number) => (
                          <OrgResourceCard
                            key={index}
                            resource={resource}
                            getResources={getResources}
                            resourceCategories={resourceCategories}
                            onAddResourceCategory={onClickAddResourceCategory}
                            setOrgToSave={setOrgToSave}
                            setShowToast={setShowToast}
                            setToastText={setToastText}
                            setToastBody={setToastBody}
                            setErrMsg={setErrMsg}
                          />
                        ))
                      )}
                    </>
                  )}
                </Col>
              </Row>
            </>
          )}
          {resources.length === 0 && !showAddResource && (
            <Row className="mt-3">
              <Col xs={6} lg={4}>
                <Button className="app-tertiary-color button" onClick={() => onClickAddResource()}>
                  Add Resource
                </Button>
              </Col>
            </Row>
          )}
          {showAddResource && (
            <AddResource
              setOrgToSave={setOrgToSave}
              disableType={disableType}
              type={resourceType}
              category={filter}
              setShowAddResource={setShowAddResource}
              setShowAddConsumable={setShowAddConsumable}
              setShowAddNonConsumable={setShowAddNonConsumable}
              getResources={getResources}
              resourceCategories={resourceCategories}
              getResourceCategories={getResourceCategories}
              onAddResourceCategory={onClickAddResourceCategory}
              setErrMsg={setErrMsg}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
            />
          )}
        </>
      )}
      <SaveChangesModal showModal={showModal} setShowModal={setShowModal} onLeave={onClickAddResourceCancel} />
    </>
  );
};

export default OrgResources;
